import { LoaderFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { queryData } from '~/api/data/request';
import { loadQuery } from '~/api/data/responses';
import {
  CaraWorkspaceArtifactFilter,
  CaraWorkspaceArtifactSummaryFieldsFragment,
  CaraWorkspaceArtifactSummaryWithContentFieldsFragment,
  ClientDocumentSortField,
  GetClientFilesDocument,
  GetClientFilesQuery,
  GetClientFilesQueryVariables,
  SearchExistingFilesDocument,
  SearchExistingFilesQuery,
  SearchExistingFilesQueryVariables,
  SearchFileResultFieldsFragment,
  SortDirection,
  GetCaraWorkspaceArtifactDocument,
  GetCaraWorkspaceArtifactQuery,
  GetCaraWorkspaceArtifactQueryVariables,
  GetCaraWorkspaceArtifactsDocument,
  GetCaraWorkspaceArtifactsQuery,
  GetCaraWorkspaceArtifactsQueryVariables
} from '~/api/graphql';

/** The launch client's latest uploads, shaped like general file-search results. */
export const getLaunchClientFiles = (id: string, search: string) =>
  queryData<GetClientFilesQuery, GetClientFilesQueryVariables, SearchFileResultFieldsFragment[]>(
    client,
    {
      query: GetClientFilesDocument,
      variables: {
        id,
        first: 5,
        filter: search.trim() ? { search: search.trim() } : undefined,
        sort: { field: ClientDocumentSortField.CreatedAt, direction: SortDirection.Desc }
      }
    },
    (data) =>
      (data?.client?.files.edges ?? []).map(({ node }) => ({
        __typename: 'SearchFileResult' as const,
        id: node.id,
        name: node.name,
        integration: null
      }))
  );

export const searchExistingFiles = (search: string) =>
  queryData<
    SearchExistingFilesQuery,
    SearchExistingFilesQueryVariables,
    SearchFileResultFieldsFragment[]
  >(
    client,
    {
      query: SearchExistingFilesDocument,
      variables: { filters: { search } }
    },
    (data) => data?.searchFiles ?? []
  );

const getCaraWorkspaceArtifactsWithFilter = async (filter?: CaraWorkspaceArtifactFilter) =>
  queryData<
    GetCaraWorkspaceArtifactsQuery,
    GetCaraWorkspaceArtifactsQueryVariables,
    CaraWorkspaceArtifactSummaryFieldsFragment[]
  >(
    client,
    { query: GetCaraWorkspaceArtifactsDocument, variables: { filter } },
    (data) => data?.caraWorkspaceArtifacts || []
  );

export const getCaraWorkspaceArtifacts = async () => getCaraWorkspaceArtifactsWithFilter();

export const getArchivedCaraWorkspaceArtifacts = async () =>
  getCaraWorkspaceArtifactsWithFilter({ archived: true });

export const getCaraWorkspaceArtifact = async (workspaceId: string, artifactId: string) =>
  queryData<
    GetCaraWorkspaceArtifactQuery,
    GetCaraWorkspaceArtifactQueryVariables,
    CaraWorkspaceArtifactSummaryWithContentFieldsFragment
  >(
    client,
    {
      query: GetCaraWorkspaceArtifactDocument,
      variables: { workspaceId: workspaceId, artifactId: artifactId }
    },
    (data) => data?.caraWorkspaceArtifact
  );

export const loadCaraWorkspaceArtifact: LoaderFunction = ({ params }) =>
  loadQuery(getCaraWorkspaceArtifact(params.workspaceId || '', params.artifactId || ''));

export const loadCaraWorkspaceArtifacts: LoaderFunction = () =>
  loadQuery(getCaraWorkspaceArtifacts(), { suppressAuthErrors: true });

export const loadArchivedCaraWorkspaceArtifacts: LoaderFunction = () =>
  loadQuery(getArchivedCaraWorkspaceArtifacts(), { suppressAuthErrors: true });
